/**
 * AuthGate - Suspense boundary for auth initialization.
 *
 * Suspends until auth initialization completes and a user is available.
 * Must be used inside a Suspense boundary.
 *
 * This component is separate from auth providers to avoid a deadlock:
 * - Providers initialize auth in useEffect (runs after render)
 * - Suspending during render would prevent effects from running
 * - By separating, the provider renders first (effects run), then gate suspends
 *
 * Usage:
 * ```tsx
 * <AuthCookieProvider>
 *   <Suspense fallback={<Loading />}>
 *     <AuthGate>
 *       <App />
 *     </AuthGate>
 *   </Suspense>
 * </AuthCookieProvider>
 * ```
 */

import type { ReactNode } from "react";

import { useSuspendingAuthUser } from "./hooks";

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps): ReactNode {
  useSuspendingAuthUser();
  return <>{children}</>;
}
